import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormRecord,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { Subject, skip, takeUntil } from 'rxjs';
import axios from 'axios';
import { addMinutes } from 'date-fns';
import {
  FormFactory,
  SZAMLAZZHU_INVOICES_FORM_NAME,
} from 'src/app/services/form/form.factory';
import {
  SzamlazzhuInvoiceForm,
  SzamlazzhuInvoicesForm,
} from 'src/app/services/form/form.controls';
import { SzamlazzhuInvoice } from 'src/app/services/form/form.resources';
import { FormService } from 'src/app/services/form/form.service';
import {
  GetKsziInfoResponse,
  InvoiceMissingReason,
  SzamlazzhuService,
} from 'src/app/services/szamlazzhu/szamlazzhu.service';
import { environment } from 'src/environments/environment';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatTooltip } from '@angular/material/tooltip';
import { NgTemplateOutlet, DecimalPipe, DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

function isKsziNotReadyAxiosError(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    error.response?.status === 400 &&
    (error.response.data as { code?: string } | undefined)?.code ===
      'kszi-not-ready'
  );
}

@Component({
  selector: 'app-szamlazzhu-invoices-form',
  host: { 'data-testid': 'szamlazzhu-invoices-form' },
  templateUrl: './szamlazzhu-invoices-form.component.html',
  styleUrls: ['./szamlazzhu-invoices-form.component.scss'],
  imports: [
    MatProgressSpinner,
    MatIcon,
    PayeeFormFieldComponent,
    FormsModule,
    ReactiveFormsModule,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatCheckbox,
    MatTooltip,
    MatSortHeader,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    NgTemplateOutlet,
    DecimalPipe,
    DatePipe,
    TranslateModule,
  ],
})
export class SzamlazzhuInvoicesFormComponent implements OnInit, OnDestroy {
  private formService = inject(FormService);
  private szamlazzhuService = inject(SzamlazzhuService);
  private fb = inject(FormBuilder);
  private invoicesLoadingCounter = 0;
  /**
   * Hack to force filterPredicate to run even when no invoiceFilter is empty
   */
  private readonly defaultFilter = 'WUxdUEFVsOTxGtAJ';
  private ksziInfo?: GetKsziInfoResponse;
  private ksziInterval?: NodeJS.Timeout;
  private validInvoices: SzamlazzhuInvoice[] = [];
  private invalidInvoices: SzamlazzhuInvoice[] = [];
  private readonly destroy = new Subject<void>();

  get invoicesLoading(): boolean {
    return this.invoicesLoadingCounter > 0 || this.formService.saving.value > 0;
  }

  get form(): SzamlazzhuInvoicesForm {
    return this.formService.form.controls[SZAMLAZZHU_INVOICES_FORM_NAME]!;
  }

  readonly invoiceFilter = this.fb.control('', { nonNullable: true });
  readonly invoiceSelectorForm: FormRecord<
    ReturnType<typeof this.createInvoiceSelectorGroup>
  > = this.fb.group({});

  get hasMultipleValidInvoices(): boolean {
    return this.validInvoicesDataSource.data.length > 1;
  }

  get showInvalidInvoicesSection(): boolean {
    return this.invalidInvoicesDataSource.data.length > 0;
  }

  ksziNotReady = false;
  get ksziSyncInProgress(): boolean {
    return (
      (this.ksziInfo?.invoices_pending ?? 0) > 0 ||
      (this.ksziInfo?.minutes_left ?? 0) > 0
    );
  }
  get showKsziLoadingBanner(): boolean {
    return !this.ksziNotReady && (!this.ksziInfo || this.ksziSyncInProgress);
  }
  get ksziEstimatedFinishTime(): Date | null {
    if (!this.ksziInfo) {
      return null;
    }

    return addMinutes(new Date(), this.ksziInfo.minutes_left);
  }
  get invoiceMissingReason(): InvoiceMissingReason | undefined {
    return this.ksziInfo?.invoice_missing_reason;
  }
  get invoiceMissingNumber(): string | null | undefined {
    return this.ksziInfo?.invoice_missing_number;
  }

  readonly validInvoicesDataSource =
    new MatTableDataSource<SzamlazzhuInvoice>();
  readonly invalidInvoicesDataSource =
    new MatTableDataSource<SzamlazzhuInvoice>();
  readonly invoicePageSize = 50;
  readonly invoicePageSizeOptions = [10, 25, 50, 100] as const;
  readonly displayedColumns = [
    'selected',
    'invoice_number',
    'due_date_at',
    'remaining_amount',
    'file',
  ] as const;

  @ViewChild('validSort') set validSort(sort: MatSort) {
    if (this.validInvoicesDataSource.sort === sort) {
      return;
    }
    this.validInvoicesDataSource.sort = sort;
  }
  @ViewChild('validPaginator') set validPaginator(paginator: MatPaginator) {
    if (this.validInvoicesDataSource.paginator === paginator) {
      return;
    }
    this.validInvoicesDataSource.paginator = paginator;
  }
  @ViewChild('invalidSort') set invalidSort(sort: MatSort) {
    if (this.invalidInvoicesDataSource.sort === sort) {
      return;
    }
    this.invalidInvoicesDataSource.sort = sort;
  }
  @ViewChild('invalidPaginator') set invalidPaginator(paginator: MatPaginator) {
    if (this.invalidInvoicesDataSource.paginator === paginator) {
      return;
    }
    this.invalidInvoicesDataSource.paginator = paginator ?? null;
  }

  async ngOnInit(): Promise<void> {
    this.setupInvoiceDataSource(this.validInvoicesDataSource);
    this.setupInvoiceDataSource(this.invalidInvoicesDataSource);

    this.setInvoicesFromForm();

    try {
      const caseId = this.formService.caseId;
      if (caseId) {
        this.ksziInfo = await this.szamlazzhuService.getKsziInfo(caseId);
        this.ksziNotReady = false;
      }
    } catch (error) {
      if (isKsziNotReadyAxiosError(error)) {
        this.ksziNotReady = true;
        this.ksziInfo = undefined;
      } else {
        console.error('Error while getting kszi info', error);
      }
    }

    await this.getInvoices();
    this.scheduleKsziInfo();

    this.formService.saving.pipe(skip(1), takeUntil(this.destroy)).subscribe({
      next: async (s) => {
        if (s <= 0) {
          await this.getInvoices();
        }
      },
    });

    this.invoiceFilter.valueChanges
      .pipe(takeUntil(this.destroy))
      .subscribe((value) => {
        this.applyInvoiceFilter(value);
      });
  }

  ngOnDestroy(): void {
    clearInterval(this.ksziInterval);
    this.destroy.next();
    this.destroy.complete();
  }

  getInvoiceSelector(invoice: SzamlazzhuInvoice): FormGroup | null {
    const control = this.invoiceSelectorForm.get(invoice.invoice_id);
    return control instanceof FormGroup ? control : null;
  }

  isInvoiceRowClickable(invoice: SzamlazzhuInvoice): boolean {
    const selector = this.getInvoiceSelector(invoice);
    return !!selector?.enabled && !this.invoicesLoading;
  }

  invoiceHasCorrections(invoice: SzamlazzhuInvoice): boolean {
    return (invoice.corrections?.length ?? 0) > 0;
  }

  onInvoiceRowClick(invoice: SzamlazzhuInvoice, event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }
    if (
      target.closest('mat-checkbox') ||
      target.closest('a') ||
      target.closest('input[type="file"]')
    ) {
      return;
    }
    const selector = this.getInvoiceSelector(invoice);
    if (!selector?.enabled || this.invoicesLoading) {
      return;
    }
    void this.toggleInvoice(invoice);
  }

  async toggleInvoice(invoice: SzamlazzhuInvoice): Promise<void> {
    if (this.invoicesLoading) {
      console.warn('Invoice toggle not allowed - loading');
      return;
    }

    const invoiceForm = this.getInvoiceSelector(invoice);
    if (!invoiceForm) {
      console.warn('Invoice form not found');
      return;
    }

    const value = invoiceForm.getRawValue();

    if (!value.selected) {
      if (!value.file) {
        try {
          const file = await this.getInvoiceFile(invoice);
          if (file !== undefined) {
            invoice.file = file;
            invoiceForm.patchValue({
              file: invoice.file,
            });
          }
        } catch (error) {
          console.error('Error while getting invoice file', error);
        }
      }

      const form = new FormFactory(this.formService, this.fb, invoice).getForm(
        'szamlazzhu-invoice',
      ) as SzamlazzhuInvoiceForm;
      this.form.push(form);
      invoiceForm.patchValue({
        index: this.form.length - 1,
        selected: true,
      });
    } else {
      if (value.index != null) {
        this.form.removeAt(value.index);
      }
      invoiceForm.patchValue({
        index: null,
        selected: false,
      });
    }
    this.form.markAsDirty();
  }

  uploadInvoice(event: Event, invoice: SzamlazzhuInvoice): void {
    if (invoice.file && !(invoice.file instanceof File)) {
      console.warn("Can't replace already uploaded file");
      return;
    }

    const inputElement = event.target as HTMLInputElement;
    const files = inputElement.files;
    if (!files || files.length <= 0) {
      return;
    }

    const file = files.item(0);
    if (!file) return;
    invoice.file = file;
    inputElement.value = '';

    const invoiceForm = this.getInvoiceSelector(invoice);
    if (!invoiceForm) {
      return;
    }

    invoiceForm.patchValue({
      file,
    });
    const { index } = invoiceForm.getRawValue();
    if (index != null) {
      this.form.at(index)?.patchValue({
        file,
      });
    }
  }

  removePendingFile(event: Event, invoice: SzamlazzhuInvoice): void {
    event.stopPropagation();

    invoice.file = null;
    const invoiceForm = this.getInvoiceSelector(invoice);
    if (!invoiceForm) {
      return;
    }

    invoiceForm.patchValue({
      file: null,
    });

    const { index } = invoiceForm.getRawValue();
    if (index != null) {
      this.form.at(index)?.patchValue({
        file: null,
      });
    }
  }

  // Set datasource from the already selected invoices
  private setInvoicesFromForm(): void {
    const formData = this.formService.forms.find(
      (f) => f.name === SZAMLAZZHU_INVOICES_FORM_NAME,
    );
    if (formData) {
      this.validInvoicesDataSource.data = [
        ...(formData.data as SzamlazzhuInvoice[]),
      ];
      for (let i = 0; i < this.validInvoicesDataSource.data.length; ++i) {
        const invoice = this.validInvoicesDataSource.data[i];
        let invoiceForm = this.getInvoiceSelector(invoice);
        if (!invoiceForm) {
          invoiceForm = this.createInvoiceSelectorGroup(invoice, {
            selected: true,
            index: i,
          });
          this.invoiceSelectorForm.setControl(invoice.invoice_id, invoiceForm);
        } else {
          invoiceForm.patchValue({
            index: i,
            selected: true,
            file: invoice.file,
          });
        }
      }
    }
  }

  private createInvoiceSelectorGroup(
    invoice: SzamlazzhuInvoice,
    { selected, index }: { selected: boolean; index: number | null },
  ) {
    return this.fb.group({
      selected: this.fb.control(selected, { nonNullable: true }),
      file: this.fb.control<SzamlazzhuInvoice['file'] | null>(invoice.file),
      index: this.fb.control<number | null>(index),
    });
  }

  private async getInvoices(): Promise<void> {
    try {
      ++this.invoicesLoadingCounter;
      const url = `${environment.baseUrl}/api/case-form/${this.formService.caseId}/szamlazzhu/invoice`;
      const result = await axios.get<{ invoices: SzamlazzhuInvoice[] }>(url);
      this.setInvoiceDatasource(result.data.invoices);
    } catch (error) {
      console.error('Error while getting szamlazzhu invoices', error);
    } finally {
      --this.invoicesLoadingCounter;
    }
  }

  private setInvoiceDatasource(invoices: SzamlazzhuInvoice[]): void {
    /**
     * We assign the invoices in this order to prevent the rows from jumping around.
     *
     * 1. Add invoices that were already selected
     * 2. Get all available invoices
     *    - If the invoice is new add it to the end of the list
     *    - If the invoice is already added update its contents
     */
    for (const invoice of invoices) {
      let invoiceForm = this.getInvoiceSelector(invoice);
      if (!invoiceForm) {
        invoiceForm = this.createInvoiceSelectorGroup(invoice, {
          selected: false,
          index: null,
        });
        this.invoiceSelectorForm.setControl(invoice.invoice_id, invoiceForm);
      } else {
        invoiceForm.patchValue({
          file: invoice.file,
        });
      }

      if (invoice.error_code) {
        invoiceForm.disable();
        const validIndex = this.validInvoices.findIndex(
          (i) => i.invoice_id === invoice.invoice_id,
        );
        if (validIndex !== -1) {
          this.validInvoices.splice(validIndex, 1);
        }
        const invalidIndex = this.invalidInvoices.findIndex(
          (i) => i.invoice_id === invoice.invoice_id,
        );
        if (invalidIndex === -1) {
          this.invalidInvoices.push(invoice);
        } else {
          this.invalidInvoices[invalidIndex] = invoice;
        }
      } else {
        invoiceForm.enable();
        const invalidIndex = this.invalidInvoices.findIndex(
          (i) => i.invoice_id === invoice.invoice_id,
        );
        if (invalidIndex !== -1) {
          this.invalidInvoices.splice(invalidIndex, 1);
        }
        const validIndex = this.validInvoices.findIndex(
          (i) => i.invoice_id === invoice.invoice_id,
        );
        if (validIndex === -1) {
          this.validInvoices.push(invoice);
        } else {
          this.validInvoices[validIndex] = invoice;
        }
      }
    }

    this.validInvoicesDataSource.data = this.validInvoices;
    this.invalidInvoicesDataSource.data = this.invalidInvoices;
  }

  private async getInvoiceFile(
    invoice: SzamlazzhuInvoice,
  ): Promise<SzamlazzhuInvoice['file'] | undefined> {
    try {
      ++this.invoicesLoadingCounter;
      const caseId = this.formService.caseId;
      if (!caseId) return undefined;
      const url = `${environment.baseUrl}/api/case-form/${caseId}/szamlazzhu/invoice-file/${invoice.invoice_id}`;
      const result = await axios.get<{ file: SzamlazzhuInvoice['file'] }>(url);
      return result.data.file;
    } catch (error) {
      console.error('Error while getting szamlazzhu invoice file', error);
      return undefined;
    } finally {
      --this.invoicesLoadingCounter;
    }
  }

  private scheduleKsziInfo(): void {
    this.ksziInterval = setInterval(async () => {
      try {
        const oldRecieved = this.ksziInfo?.invoices_recieved ?? 0;
        this.ksziInfo = await this.szamlazzhuService.getKsziInfo(
          this.formService.caseId!,
        );
        this.ksziNotReady = false;
        const newRecieved = this.ksziInfo?.invoices_recieved ?? 0;
        if (newRecieved != oldRecieved) {
          await this.getInvoices();
        }
      } catch (error) {
        console.error('Error while getting kszi info', error);
      }
    }, 10_000);
  }

  private setupInvoiceDataSource(
    ds: MatTableDataSource<SzamlazzhuInvoice>,
  ): void {
    ds.sortingDataAccessor = (data, headerId) => {
      const header = headerId as (typeof this.displayedColumns)[number];
      switch (header) {
        case 'file':
          return !data.file ? 0 : data.file instanceof File ? 1 : 2;
        case 'remaining_amount':
          return data.total_remaining_amount;
        case 'selected':
          return 0;
        default:
          return data[header];
      }
    };

    ds.filterPredicate = (data, filter) => {
      if (this.defaultFilter === filter) {
        return true;
      }
      return (
        data.invoice_number.includes(filter) ||
        data.corrections.some((i) => i.invoice_number.includes(filter))
      );
    };
    ds.filter = this.defaultFilter;
  }

  private applyInvoiceFilter(value: string): void {
    if (value) {
      this.validInvoicesDataSource.filter = value;
      this.invalidInvoicesDataSource.filter = value;
    } else {
      this.validInvoicesDataSource.filter = this.defaultFilter;
      this.invalidInvoicesDataSource.filter = this.defaultFilter;
    }
  }
}
